import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ApiCall } from '../helper/axios';
import { triggerRefresh } from './refreshSlice';
import { toast } from '../common_components/Toast/ToastUtils';
const PARENT_MODULE_KEY = 'affiliate_account_performance';
const initialState = {
    clientId: null,
    clientName: null,
    connections: [],
    allConnections: [],
    summary: null,
    tabsWithRows: [],
    tableState: {
        sortBy: 'revenue',
        sortOrder: 'desc',
        pages: {},
        limit: 20,
    },
    lastUpdated: null,
    overallStatus: null,
    affiliateAccountType: null,
    affiliate_measurement_channel: [],
    clientType: 'View All',
    totalGoalToggle: false,
    loading: false,
    refreshLoading: false,
    error: null,
    currentRequestId: undefined,
};
export const fetchClientOverview = createAsyncThunk('accountOverview/fetchClientOverview', async (payload, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall('POST', '/api/affiliate-account-overview/report', payload, { Authorization: `Bearer ${token}` });
        if (res && res.data?.success) {
            return res.data.data;
        }
        return rejectWithValue(res?.data?.message || 'Failed to fetch analytics report');
    }
    catch (err) {
        return rejectWithValue(err?.message || 'Error fetching analytics report');
    }
});
export const refreshAccountOverview = createAsyncThunk('accountOverview/refreshAccountOverview', async (payload, { dispatch, rejectWithValue }) => {
    const result = await dispatch(triggerRefresh({
        clientId: payload.clientId,
        dateRange: [{ startDate: payload.startDate, endDate: payload.endDate }],
        moduleKey: PARENT_MODULE_KEY,
    }));
    if (triggerRefresh.fulfilled.match(result)) {
        const failedNetworks = result.payload?.data?.failed_networks || [];
        if (failedNetworks.length > 0) {
            const failedNames = [...new Map(failedNetworks.map((n) => [n.network, n.error])).entries()]
                .map(([name, err]) => {
                let eStr = typeof err === 'string' ? err : err?.message;
                if (eStr && eStr.toLowerCase().includes('time'))
                    eStr = 'Timeout';
                return `${name.charAt(0).toUpperCase() + name.slice(1)}${eStr ? ` (${eStr})` : ''}`;
            })
                .join(', ');
            toast.warning(`Account Overview refresh completed.\nFailed to refresh: ${failedNames}.`);
        }
        else {
            toast.success('Account Overview refresh completed successfully.');
        }
        return result.payload?.data ?? result.payload;
    }
    return rejectWithValue(result.payload || 'Failed to trigger refresh');
});
export const fetchReferralRecords = createAsyncThunk('accountOverview/fetchReferralRecords', async (payload, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall('POST', '/api/affiliate-account-overview/referral-records', payload, { Authorization: `Bearer ${token}` });
        if (res && res.data?.success) {
            return res.data.data;
        }
        return rejectWithValue(res?.data?.message || 'Failed to fetch referral records');
    }
    catch (err) {
        return rejectWithValue(err?.message || 'Error fetching referral records');
    }
});
const accountOverviewSlice = createSlice({
    name: 'accountOverview',
    initialState,
    reducers: {
        setClientContext: (state, action) => {
            state.clientId = action.payload.clientId;
            state.clientName = action.payload.clientName;
        },
        setTableSort: (state, action) => {
            state.tableState.sortBy = action.payload.sortBy;
            state.tableState.sortOrder = action.payload.sortOrder;
            state.tableState.pages = {}; // reset pages on sort change
        },
        setTabPage: (state, action) => {
            state.tableState.pages[action.payload.tabKey] = action.payload.page;
        },
        setClientType: (state, action) => {
            state.clientType = action.payload;
        },
        setTotalGoalToggle: (state, action) => {
            state.totalGoalToggle = action.payload;
        },
        resetAccountOverview: (state) => {
            state.clientId = null;
            state.clientName = null;
            state.connections = [];
            state.allConnections = [];
            state.summary = null;
            state.tabsWithRows = [];
            state.tableState = { sortBy: 'revenue', sortOrder: 'desc', pages: {}, limit: 10 };
            state.lastUpdated = null;
            state.overallStatus = null;
            state.affiliate_measurement_channel = [];
            state.refreshLoading = false;
            state.loading = false;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchClientOverview.pending, (state, action) => {
            state.loading = true;
            state.error = null;
            state.currentRequestId = action.meta.requestId;
        })
            .addCase(fetchClientOverview.fulfilled, (state, action) => {
            if (state.currentRequestId !== action.meta.requestId) {
                return; // Ignore if it's not the latest request
            }
            state.loading = false;
            state.connections = action.payload.connections || [];
            state.summary = action.payload.summary || null;
            state.tabsWithRows = action.payload.tabs_with_rows || [];
            state.lastUpdated = action.payload.lastUpdated || null;
            state.overallStatus = action.payload.overallStatus || null;
            state.affiliateAccountType = action.payload.affiliate_account_type || null;
            state.affiliate_measurement_channel = action.payload.affiliate_measurement_channel || [];
            // Automatically set clientType to 'Custom Client' if the backend detects it
            if (action.payload.is_custom_client) {
                state.clientType = 'Custom Client';
            }
            if (!action.meta.arg.connectionId) {
                state.allConnections = action.payload.connections || [];
            }
            if (action.payload.clientDetails?.name) {
                state.clientName = action.payload.clientDetails.name;
            }
        })
            .addCase(fetchClientOverview.rejected, (state, action) => {
            if (state.currentRequestId !== action.meta.requestId) {
                return; // Ignore if it's not the latest request
            }
            state.loading = false;
            state.error = action.payload;
        })
            .addCase(refreshAccountOverview.pending, (state) => {
            state.refreshLoading = true;
        })
            .addCase(refreshAccountOverview.fulfilled, (state) => {
            state.refreshLoading = false;
        })
            .addCase(refreshAccountOverview.rejected, (state) => {
            state.refreshLoading = false;
        });
    },
});
export const { setClientContext, resetAccountOverview, setTableSort, setTabPage, setClientType, setTotalGoalToggle } = accountOverviewSlice.actions;
export default accountOverviewSlice.reducer;
